import { Component, h, Host, Element, Prop } from "@stencil/core";

/**
 * @slot title - Slot for the product title (`<h3>` or `<h2>`)
 * @slot subtitle - Slot for the product subtitle (`<p>` or `<div>`)
 * @slot image - Slot for the product image (`<picture>` with 3:2 aspect ratio)
 * @slot description - Slot for the product description (`<p>` or `<div>`)
 * @slot features - Slot for the list with product key features (`<ul>`)
 * @slot cta - call to action (`<ux-button>`)
 */
@Component({
    tag: "ux-iws-nb-teaser-product",
    styleUrl: "nb-teaser-product.scss",
})
export class NbTeaserProduct {
    private static uidIncrement = 0;
    private uid = `nb-teaser-product-${NbTeaserProduct.uidIncrement++}`;
    private headingLevel: number = 3;

    @Element() host: HTMLUxIwsNbTeaserProductElement;

    /** Comma separated list of tags */
    @Prop() tags: string = "";
    /** Label for the list of supported tags (accessibility) */
    @Prop() tagsLabel: string = "Supported Applications";
    /** Comma separated list of tags that are highlighted */
    @Prop() highlightedTags: string = "";
    /** Label for the list of comparison criteria (accessibility) */
    @Prop() featuresLabel: string = "List of comparison criteria";

    private detectHeadingLevel(): number {
        const titleSlot = this.host.querySelector("[slot='title']");
        const headingElement =
            titleSlot.querySelector("h2, h3, h4") || titleSlot;
        let headlingLevel = parseInt(headingElement.nodeName.replace("H", ""));
        if (
            Number.isNaN(headlingLevel) ||
            headlingLevel < 2 ||
            headlingLevel > 4
        ) {
            headlingLevel = 3;
        }
        return headlingLevel;
    }

    componentWillLoad() {
        this.headingLevel = this.detectHeadingLevel();
    }

    render() {
        const baseClass = "ux-iws-nb-teaser-product";
        const allTags = this.tags.split(",").map((tag) => tag.trim());
        const highlightedTags = this.highlightedTags
            .split(",")
            .map((tag) => tag.trim());
        const SubHeadingTag = `h${this.headingLevel + 1}`;
        return (
            <Host class={baseClass}>
                <div class="ux-row ux-u-mx-zero">
                    <div class="ux-col-12 ux-col-lg-10 ux-u-offset-lg-1 ux-u-px-lg-zero">
                        <article
                            class={`${baseClass}__grid`}
                            aria-labelledby={`${this.uid}-title`}
                        >
                            <div class={`${baseClass}__image`}>
                                <slot name="image" />
                            </div>
                            <header class={`${baseClass}__header`}>
                                <div id={`${this.uid}-title`}>
                                    <slot name="title" />
                                </div>
                                <div class={`${baseClass}__subtitle`}>
                                    <slot name="subtitle" />
                                </div>
                            </header>
                            <section
                                class={`${baseClass}__tags`}
                                aria-labelledby={`${this.uid}-tags-label`}
                            >
                                <SubHeadingTag
                                    id={`${this.uid}-tags-label`}
                                    class="visually-hidden"
                                >
                                    {this.tagsLabel}
                                </SubHeadingTag>
                                {allTags.map((tag) => (
                                    <span
                                        class={{
                                            [`${baseClass}__tag`]: true,
                                            [`${baseClass}__tag--highlighted`]:
                                                highlightedTags.includes(tag),
                                        }}
                                        key={tag}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </section>
                            <div class={`${baseClass}__description`}>
                                <slot name="description" />
                                <slot name="cta" />
                            </div>
                            <section
                                class={`${baseClass}__features`}
                                aria-labelledby={`${this.uid}-features-label`}
                            >
                                <SubHeadingTag
                                    id={`${this.uid}-features-label`}
                                    class="visually-hidden"
                                >
                                    {this.featuresLabel}
                                </SubHeadingTag>
                                <slot name="features" />
                            </section>
                            <div class={`${baseClass}__cta`}>
                                <slot name="cta" />
                            </div>
                        </article>
                    </div>
                </div>
            </Host>
        );
    }
}
